import React, { useEffect, useState } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import Header from '../../components/ui/Header';
import Footer from '../../components/ui/Footer';
import CarCard from './components/CarCard';
import { calculateCityToCity, VEHICLE_NAMES } from '../../utils/pricingCalculator';
import Icon from '../../components/AppIcon';

const SearchResults = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const searchData = location?.state;
  const [vehicleResults, setVehicleResults] = useState([]);

  useEffect(() => {
    window.scrollTo(0, 0);
  }, []);

  useEffect(() => {
    if (!searchData?.from || !searchData?.to) {
      navigate('/');
      return;
    }

    // Calculate pricing for all vehicle types
    const vehicles = [
      { key: 'marutiDzire', seats: 4, luggage: 3, image: 'https://img.rocket.new/generatedImages/rocket_gen_img_1bc20140d-1768380108457.png', alt: 'White Maruti Dzire sedan car with sleek design parked on city street' },
      { key: 'brezza', seats: 4, luggage: 4, image: 'https://img.rocket.new/generatedImages/rocket_gen_img_16b493177-1768380107788.png', alt: 'Maruti Brezza compact SUV in urban setting with modern design' },
      { key: 'marutiErtiga', seats: 6, luggage: 4, image: 'https://img.rocket.new/generatedImages/rocket_gen_img_10a91d818-1764720425845.png', alt: 'Maruti Ertiga MPV with spacious seating capacity parked outdoors' },
      { key: 'kiaCarens', seats: 6, luggage: 5, image: 'https://img.rocket.new/generatedImages/rocket_gen_img_10c28221a-1768075340455.png', alt: 'Kia Carens premium MPV with modern styling and comfortable interiors' },
      { key: 'innovaCrysta', seats: 6, luggage: 6, image: 'https://img.rocket.new/generatedImages/rocket_gen_img_144c4e8d9-1766473631173.png', alt: 'Toyota Innova Crysta luxury MPV in silver color with premium features' },
      { key: 'innovaCrysta7Plus1', seats: 7, luggage: 6, image: 'https://img.rocket.new/generatedImages/rocket_gen_img_10b3582ff-1764720422661.png', alt: 'Pearl white Toyota Innova Crysta 7+1 seater with extra luggage capacity' },
      { key: 'innovaHycross', seats: 6, luggage: 5, image: 'https://img.rocket.new/generatedImages/rocket_gen_img_1ece112f8-1768368473505.png', alt: 'White Toyota Innova Hycross luxury SUV with modern design' }
    ];

    const results = vehicles?.map(vehicle => {
      const pricing = calculateCityToCity(searchData?.fromCity, searchData?.toCity, vehicle?.key);
      return {
        id: vehicle?.key,
        name: VEHICLE_NAMES?.[vehicle?.key],
        seats: vehicle?.seats,
        luggage: vehicle?.luggage,
        price: pricing?.totalFare,
        baseFare: pricing?.baseFare,
        toll: pricing?.toll,
        distance: pricing?.distance,
        comparison: pricing?.comparison,
        image: vehicle?.image,
        imageAlt: vehicle?.alt
      };
    });

    setVehicleResults(results);
  }, [searchData, navigate]);

  const handleSelectCar = (vehicle) => {
    navigate('/booking-confirmation', {
      state: {
        vehicle,
        route: {
          from: searchData?.from,
          to: searchData?.to,
          date: searchData?.date
        }
      }
    });
  };

  return (
    <div className="min-h-screen bg-background">
      <Header />
      
      <main className="container mx-auto px-4 pt-16 pb-8">
        {/* Route Header */}
        <div className="bg-gradient-to-r from-teal-600 to-orange-500 rounded-xl p-6 mb-8 text-white">
          <div className="flex items-center justify-between flex-wrap gap-4">
            <div>
              <h1 className="text-3xl font-bold mb-2">
                {searchData?.from} to {searchData?.to}
              </h1>
              <div className="flex items-center gap-4 text-white/90">
                <div className="flex items-center gap-2">
                  <Icon name="Calendar" size={18} />
                  <span>{searchData?.date}</span>
                </div>
                {vehicleResults?.[0] && (
                  <>
                    <div className="flex items-center gap-2">
                      <Icon name="Navigation" size={18} />
                      <span>{vehicleResults?.[0]?.distance} km</span>
                    </div>
                    <div className="flex items-center gap-2">
                      <Icon name="IndianRupee" size={18} />
                      <span>Toll: ₹{vehicleResults?.[0]?.toll}</span>
                    </div>
                  </>
                )}
              </div>
            </div>
            <button
              onClick={() => navigate('/')}
              className="bg-white text-teal-600 px-6 py-2 rounded-lg font-semibold hover:bg-gray-100 transition-colors"
            >
              Modify Search
            </button>
          </div>
        </div>

        {/* Price Comparison Banner */}
        {vehicleResults?.[0]?.comparison && (
          <div className="bg-green-50 border border-green-200 rounded-lg p-4 mb-8">
            <div className="flex items-center gap-3">
              <Icon name="TrendingDown" className="text-green-600" size={24} />
              <div>
                <h3 className="font-semibold text-green-800">Best Price Guarantee!</h3>
                <p className="text-sm text-green-700">
                  Save up to ₹{vehicleResults?.[0]?.comparison?.savings} compared to OLA (₹{vehicleResults?.[0]?.comparison?.olaPrice}) and Uber (₹{vehicleResults?.[0]?.comparison?.uberPrice})
                </p>
              </div>
            </div>
          </div>
        )}

        {/* Vehicle Results */}
        <div className="space-y-6">
          <h2 className="text-2xl font-bold text-foreground mb-4">
            Available Vehicles ({vehicleResults?.length})
          </h2>
          
          {vehicleResults?.map((vehicle) => (
            <div key={vehicle?.id} className="relative">
              <CarCard
                vehicle={vehicle}
                onSelect={() => handleSelectCar(vehicle)}
              />
              
              {/* Pricing Details */}
              <div className="mt-2 bg-gray-50 rounded-lg p-4 text-sm">
                <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                  <div>
                    <span className="text-gray-600">Base Fare:</span>
                    <span className="ml-2 font-semibold">₹{vehicle?.baseFare}</span>
                  </div>
                  <div>
                    <span className="text-gray-600">Toll:</span>
                    <span className="ml-2 font-semibold">₹{vehicle?.toll}</span>
                  </div>
                  <div>
                    <span className="text-gray-600">OLA Price:</span>
                    <span className="ml-2 font-semibold text-red-600">₹{vehicle?.comparison?.olaPrice}</span>
                  </div>
                  <div>
                    <span className="text-gray-600">Uber Price:</span>
                    <span className="ml-2 font-semibold text-red-600">₹{vehicle?.comparison?.uberPrice}</span>
                  </div>
                </div>
                <div className="mt-2 text-green-600 font-semibold">
                  You Save: ₹{vehicle?.comparison?.savings} with us!
                </div>
              </div>
            </div>
          ))}
        </div>

        {/* Features Section */}
        <div className="mt-12 grid grid-cols-1 md:grid-cols-3 gap-6">
          <div className="bg-white rounded-lg p-6 shadow-md">
            <Icon name="Shield" className="text-teal-600 mb-3" size={32} />
            <h3 className="font-bold text-lg mb-2">Safe & Secure</h3>
            <p className="text-gray-600 text-sm">Verified drivers and GPS-tracked rides for your safety</p>
          </div>
          <div className="bg-white rounded-lg p-6 shadow-md">
            <Icon name="Clock" className="text-orange-600 mb-3" size={32} />
            <h3 className="font-bold text-lg mb-2">24/7 Service</h3>
            <p className="text-gray-600 text-sm">Round-the-clock availability for all your travel needs</p>
          </div>
          <div className="bg-white rounded-lg p-6 shadow-md">
            <Icon name="BadgeCheck" className="text-teal-600 mb-3" size={32} />
            <h3 className="font-bold text-lg mb-2">Best Rates</h3>
            <p className="text-gray-600 text-sm">Competitive pricing with transparent toll charges</p>
          </div>
        </div>
      </main>

      <Footer />
    </div>
  );
};

export default SearchResults;